"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";

import type { IDistrictSchoolListItem } from "@/providers/store/api/districts/districts.interface";

export type UpdateBoundarySchoolFn = (
  boundaryId: string,
  schools: readonly IDistrictSchoolListItem[],
) => void;

interface BoundaryPanelActionsContextValue {
  updateBoundarySchool: UpdateBoundarySchoolFn;
  isUpdateBoundarySchoolActive: boolean;
  setUpdateBoundarySchool: (fn: UpdateBoundarySchoolFn | null) => void;
}

const BoundaryPanelActionsContext =
  createContext<BoundaryPanelActionsContextValue | null>(null);

export function useBoundaryPanelActions() {
  const context = useContext(BoundaryPanelActionsContext);
  if (!context) {
    throw new Error(
      "useBoundaryPanelActions must be used inside <BoundaryPanelActionsProvider />",
    );
  }
  return context;
}

export function useBoundaryPanelActionsOptional() {
  return useContext(BoundaryPanelActionsContext);
}

export function useSetBoundaryPanelActions() {
  const context = useContext(BoundaryPanelActionsContext);
  if (!context) {
    return null;
  }
  return context.setUpdateBoundarySchool;
}

export const BoundaryPanelActionsProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const updateBoundarySchoolRef = useRef<UpdateBoundarySchoolFn | null>(null);
  const [isUpdateBoundarySchoolActive, setIsUpdateBoundarySchoolActive] =
    useState(false);

  const updateBoundarySchool = useCallback<UpdateBoundarySchoolFn>(
    (boundaryId, schools) => {
      updateBoundarySchoolRef.current?.(boundaryId, schools);
    },
    [],
  );

  const setUpdateBoundarySchool = useCallback((fn: UpdateBoundarySchoolFn | null) => {
    const wasActive = updateBoundarySchoolRef.current != null;
    updateBoundarySchoolRef.current = fn;
    const isActive = fn != null;
    if (wasActive !== isActive) {
      setIsUpdateBoundarySchoolActive(isActive);
    }
  }, []);

  const value = useMemo<BoundaryPanelActionsContextValue>(
    () => ({
      updateBoundarySchool,
      isUpdateBoundarySchoolActive,
      setUpdateBoundarySchool,
    }),
    [isUpdateBoundarySchoolActive, setUpdateBoundarySchool, updateBoundarySchool],
  );

  return (
    <BoundaryPanelActionsContext.Provider value={value}>
      {children}
    </BoundaryPanelActionsContext.Provider>
  );
};
